import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, setHeaderOptions } from "./apiHelpers";

export const refreshImageByUrl = async ({
  url,
  prompt,
  newImage,
}: {
  url: string;
  prompt: string;
  newImage: boolean;
}): Promise<string> => {
  const slug = `${ApiEndpoints.mediaReference}/refresh`;
  const options = await setHeaderOptions({
    method: "POST",
    body: { url, prompt, newImage },
  });

  return fetchData<string>(slug, options);
};

export const deleteImageByUrl = async (url: string): Promise<void> => {
  const slug = `${ApiEndpoints.mediaReference}/url`;
  const options = await setHeaderOptions({
    method: "DELETE",
    body: { url },
  });

  await fetchData<void>(slug, options);
};

export const MediaUploadContexts = [
  "blog",
  "site",
  "marketing",
  "content",
  "general",
] as const;

export type MediaUploadContext = (typeof MediaUploadContexts)[number];

export type SignedUploadRequest = {
  contentType: string;
  size: number;
  fileName: string;
  context: MediaUploadContext;
  method: "post" | "put";
};

export type SignedUploadPostResponse = {
  method: "POST";
  url: string;
  fields: Record<string, string>;
  key: string;
  cdn_url: string;
  expires_at: string;
  max_size: number;
};

export type SignedUploadPutResponse = {
  method: "PUT";
  upload_url: string;
  required_headers: Record<string, string>;
  key: string;
  cdn_url: string;
  expires_at: string;
  max_size: number;
};

export type SignedUploadResponse = SignedUploadPostResponse | SignedUploadPutResponse;

export type RegisterMediaRequest = {
  key: string;
  context: MediaUploadContext;
  source: "s3";
  client_metadata?: {
    width?: number;
    height?: number;
  };
};

export type RegisterMediaResponse = {
  id: string;
  key: string;
  cdn_url: string;
  etag?: string;
  size?: number;
  mime?: string;
};

export async function requestSignedMediaUpload(
  payload: SignedUploadRequest
): Promise<SignedUploadResponse> {
  const slug = "/media/signed-upload";
  const options = await setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<SignedUploadResponse>(slug, options);
}

export async function registerMediaUpload(
  payload: RegisterMediaRequest
): Promise<RegisterMediaResponse> {
  const slug = "/media/register";
  const options = await setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<RegisterMediaResponse>(slug, options);
}

export async function uploadMediaToSignedUrl(
  file: File,
  signed: SignedUploadResponse
): Promise<void> {
  if (signed.method === "POST") {
    const form = new FormData();
    Object.entries(signed.fields).forEach(([key, value]) => {
      form.append(key, value);
    });
    form.append("file", file);

    const response = await fetch(signed.url, {
      method: "POST",
      body: form,
    });

    if (!response.ok) {
      throw new Error("Failed to upload file to S3.");
    }
    return;
  }

  const response = await fetch(signed.upload_url, {
    method: "PUT",
    headers: signed.required_headers,
    body: file,
  });

  if (!response.ok) {
    throw new Error("Failed to upload file to S3.");
  }
}

export type MediaEmbeddingResponse = {
  created: boolean;
};

export type MediaEmbeddingStatusResponse = {
  has_embedding: boolean;
};

export async function createMediaEmbedding(
  id: string
): Promise<MediaEmbeddingResponse> {
  const slug = `/media/${id}/embedding`;
  const options = await setHeaderOptions({
    method: "POST",
    body: {},
  });

  return fetchData<MediaEmbeddingResponse>(slug, options);
}

export async function getMediaEmbeddingStatus(
  id: string
): Promise<MediaEmbeddingStatusResponse> {
  const slug = `/media/${id}/embedding`;
  const options = await setHeaderOptions({
    method: "GET",
  });

  return fetchData<MediaEmbeddingStatusResponse>(slug, options);
}

export type MediaDetailResponse = {
  id: string;
  url: string;
  subject: string | null;
  name: string | null;
  slug: string | null;
  image_type: string | null;
  prompt: string | null;
  relevant_text: string | null;
  url_match: string | null;
  insert_regex: string | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  has_embedding?: boolean | null;
  size_bytes?: number | null;
  width?: number | null;
  height?: number | null;
  checksum?: string | null;
  source?: string | null;
  input_url?: string | null;
  input_mime?: string | null;
  created_by?: string | null;
};

export async function getMediaDetail(
  idOrSlug: string
): Promise<MediaDetailResponse> {
  const slug = `/media/${encodeURIComponent(idOrSlug)}?includeEmbedding=true`;
  const options = await setHeaderOptions({ method: "GET" });
  const response = await fetchData<any>(slug, options);
  const payload = response?.data ?? response ?? {};

  return {
    id: payload.id,
    url: payload.url ?? payload.cdn_url ?? "",
    subject: payload.subject ?? payload.context ?? null,
    name: payload.name ?? null,
    slug: payload.slug ?? payload.key ?? null,
    image_type: payload.image_type ?? payload.mime ?? null,
    prompt: payload.prompt ?? null,
    relevant_text: payload.relevant_text ?? null,
    url_match: payload.url_match ?? null,
    insert_regex: payload.insert_regex ?? null,
    is_active: payload.is_active ?? null,
    created_at: payload.created_at ?? null,
    updated_at: payload.updated_at ?? null,
    has_embedding: payload.has_embedding ?? null,
    size_bytes: payload.size_bytes ?? null,
    width: payload.width ?? null,
    height: payload.height ?? null,
    checksum: payload.checksum ?? null,
    source: payload.source ?? null,
    input_url: payload.input_url ?? null,
    input_mime: payload.input_mime ?? null,
    created_by: payload.created_by ?? null,
  };
}

export type MediaGenerateRequest = {
  prompt: string;
  context: MediaUploadContext;
  imageUrl?: string;
  imageBase64?: string;
  imageMimeType?: string;
  fileName: string;
  model?: string;
};

export type MediaGenerateResponse = {
  queued: boolean;
  request_id: string;
};

export type MediaGenerateStatusResponse = {
  status: "queued" | "running" | "succeeded" | "failed";
  cdn_url?: string | null;
  media_reference_id?: string | null;
  error?: string | null;
};

export async function generateMedia(
  payload: MediaGenerateRequest
): Promise<MediaGenerateResponse> {
  const slug = "/media/generate";
  const options = await setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<MediaGenerateResponse>(slug, options);
}

export async function getMediaGenerateStatus(
  requestId: string
): Promise<MediaGenerateStatusResponse> {
  const slug = `/media/generate/${requestId}/status`;
  const options = await setHeaderOptions({
    method: "GET",
  });

  return fetchData<MediaGenerateStatusResponse>(slug, options);
}
